import type { Ref } from 'vue'
import { useGeolocateOrganigramme } from '~/composables/api/useGeolocateOrganigramme'
import { useExperimentalFeatures } from '~/composables/experimental/useExperimentalFeatures'
import { useAppToolboxAvailability } from '~/composables/ui/useAppToolboxAvailability'
import { useOrganizationTreeModal } from '~/composables/organization/useOrganizationTreeModal'
import { ORGANIZATION_TREE_FEATURE_KEY } from '~~/utils/experimental/experimentalFeatures'

type UseAppLayoutToolboxParams = {
  loggedIn: Ref<boolean>
  toolboxOpen: Ref<boolean>
  isWelcomeResolved: Ref<boolean>
  showSeasonalWelcome: Ref<boolean>
  shouldShowWelcomeSplash: Ref<boolean>
  trySetEditMode: (value: boolean) => void
}

export function useAppLayoutToolbox(params: UseAppLayoutToolboxParams) {
  const { isToolboxUiBlocked, isConnectionRoute } = useAppToolboxAvailability()
  const {
    geolocateLoading,
    showGeolocateEmptyModal,
    showGeolocateErrorModal,
    geolocateErrorMessage,
    showGeolocateChoiceModal,
    geolocateChoices,
    onGeolocateOrganigramme,
    closeGeolocateChoiceModal,
    onGeolocateChoicePick,
  } = useGeolocateOrganigramme(params.loggedIn)

  const toolboxOpen = params.toolboxOpen
  const { canUse } = useExperimentalFeatures()
  const canUseOrganizationTree = computed(() => canUse(ORGANIZATION_TREE_FEATURE_KEY))
  const {
    isOpen: organizationTreeModalOpen,
    openTreeModal,
    closeTreeModal,
  } = useOrganizationTreeModal()

  watch(canUseOrganizationTree, (allowed) => {
    if (!allowed) {
      closeTreeModal()
    }
  })

  function onToolboxGeolocate(): void {
    void onGeolocateOrganigramme()
  }

  function onToolboxOpenTree(): void {
    if (!canUseOrganizationTree.value) {
      return
    }
    openTreeModal()
  }

  function onToolboxIsEditingUpdate(value: boolean): void {
    params.trySetEditMode(value)
  }

  function toggleToolboxOpen(): void {
    toolboxOpen.value = !toolboxOpen.value
  }

  function closeToolboxAndTree(): void {
    toolboxOpen.value = false
    closeTreeModal()
  }

  watch(isToolboxUiBlocked, (blocked) => {
    if (blocked) {
      closeToolboxAndTree()
    }
  })

  watch(
    () => params.showSeasonalWelcome.value,
    (open) => {
      if (open) {
        closeToolboxAndTree()
      }
    },
  )

  watch(
    () => params.shouldShowWelcomeSplash.value,
    (show) => {
      if (show) {
        closeToolboxAndTree()
      }
    },
  )

  const showToolbox = computed(
    () => params.loggedIn.value && !isToolboxUiBlocked.value && params.isWelcomeResolved.value,
  )

  return {
    isToolboxUiBlocked,
    isConnectionRoute,
    geolocateLoading,
    showGeolocateEmptyModal,
    showGeolocateErrorModal,
    geolocateErrorMessage,
    showGeolocateChoiceModal,
    geolocateChoices,
    closeGeolocateChoiceModal,
    onGeolocateChoicePick,
    toolboxOpen,
    organizationTreeModalOpen,
    canUseOrganizationTree,
    onToolboxGeolocate,
    onToolboxOpenTree,
    onToolboxIsEditingUpdate,
    toggleToolboxOpen,
    showToolbox,
  }
}
